"use client";

/**
 * The team action parked across a sign-in: Copy and Deploy pressed while logged
 * out are preserved and resumed once sign-in succeeds on the same source team.
 * Losing either would silently drop the one thing the user asked for and land
 * them on a landing page instead.
 *
 * A component-local ref only survives an in-page modal sign-in, and there is no
 * such thing here: magic link, Google and Apple all leave the origin and come
 * back through `/auth/callback`, so the intent has to outlive a full page load.
 *
 * It takes TWO things to resume, and one is useless without the other: the
 * intent (which action and team the user chose) and a landing that has that
 * team on screen to resume into. This module parks both — the intent below,
 * and the team's landing route through `lib/authReturnRoute`, the same carrier
 * the forced-sign-out flow already rides back. Park and release move together,
 * so a released intent can never leave a route behind to reopen a team nobody
 * asked for.
 *
 * `sessionStorage`, not `localStorage`, matching the return route it travels
 * with: the entry describes THIS tab's interruption, must survive the hard
 * navigation, and must die with the tab rather than teleporting an unrelated
 * future visit. `localStorage` would carry the intent into a magic link opened
 * in a fresh tab — but the return route it needs is tab-scoped by ratified
 * design, so the intent would arrive at a landing with no team open and nothing
 * to resume into, at the cost of an intent that outlives the tab that made it.
 *
 * Three guarantees the resume side depends on:
 *  - it names ONE source team, and reading it for any other team is a miss;
 *  - it is consumed exactly once (`clearParkedTeamIntent` on resume);
 *  - it expires, so an abandoned sign-in cannot resurrect an action the user
 *    has long since walked away from.
 *
 * It records an INTENT, never a write: Copy only opens Edit mode (no team record
 * exists until the global Save copy action), and Deploy only opens preflight (no
 * session exists until Start Session). An abandoned sign-in therefore cannot
 * leave a half-made team or Session behind.
 */

import {
  captureAuthReturnRoute,
  clearAuthReturnRoute,
  peekAuthReturnRoute,
} from "@/lib/authReturnRoute";
import { buildViewTeamPath } from "@/hooks/navigation/view-team-url";

const TEAM_INTENT_KEY = "cabal_teams_gallery_copy_intent";
const TEAM_INTENT_SEPARATOR = "|";

/** Long enough for the slowest sign-in the product offers — a magic link is an
 *  email round trip, read on whatever device the user reaches for — and short
 *  enough that a walked-away tab resumes nothing. */
const TEAM_INTENT_TTL_MS = 1_800_000;

export enum ParkedTeamIntentKind {
  Copy = "copy",
  Deploy = "deploy",
}

interface ParkedTeamIntent {
  kind: ParkedTeamIntentKind;
  teamId: string;
  parkedAtMs: number;
}

/**
 * The resume attempt is re-evaluated on every render of the open team modal
 * (neither target can accept the intent until the team detail lands), so the
 * check reads this mirror instead of storage. Storage is read once per page
 * load to seed it — which is exactly the read that survives the redirect.
 */
let mirroredIntent: ParkedTeamIntent | null = null;
let hasSeededMirror = false;

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Resuming the
    // intent is a convenience — never let it break the sign-in path.
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // See readSessionStorage.
  }
}

function removeSessionStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // See readSessionStorage.
  }
}

function parseParkedIntentKind(value: string): ParkedTeamIntentKind | null {
  if (value === ParkedTeamIntentKind.Copy) return ParkedTeamIntentKind.Copy;
  if (value === ParkedTeamIntentKind.Deploy) return ParkedTeamIntentKind.Deploy;
  return null;
}

function parseParkedIntent(entry: string | null): ParkedTeamIntent | null {
  if (entry == null) return null;

  const kindSeparatorIndex = entry.indexOf(TEAM_INTENT_SEPARATOR);
  if (kindSeparatorIndex === -1) return null;
  const timeSeparatorIndex = entry.indexOf(
    TEAM_INTENT_SEPARATOR,
    kindSeparatorIndex + 1,
  );
  if (timeSeparatorIndex === -1) return null;

  const kind = parseParkedIntentKind(entry.slice(0, kindSeparatorIndex));
  const parkedAtMs = Number(
    entry.slice(kindSeparatorIndex + 1, timeSeparatorIndex),
  );
  const teamId = entry.slice(timeSeparatorIndex + 1);
  if (kind == null || !Number.isFinite(parkedAtMs) || teamId.length === 0) {
    return null;
  }

  return { kind, teamId, parkedAtMs };
}

function seededIntent(): ParkedTeamIntent | null {
  if (hasSeededMirror) return mirroredIntent;
  hasSeededMirror = true;
  mirroredIntent = typeof window === "undefined"
    ? null
    : parseParkedIntent(readSessionStorage(TEAM_INTENT_KEY));
  return mirroredIntent;
}

function isExpired(intent: ParkedTeamIntent): boolean {
  return Date.now() - intent.parkedAtMs > TEAM_INTENT_TTL_MS;
}

/**
 * Park one team action immediately before handing off to sign-in, together with
 * the landing route that puts that team back on screen. A second park replaces
 * the first — there is only ever one action interruption in this tab.
 */
export function parkTeamIntent({
  kind,
  teamId,
}: {
  kind: ParkedTeamIntentKind;
  teamId: string;
}): void {
  const parkedAtMs = Date.now();
  mirroredIntent = { kind, teamId, parkedAtMs };
  hasSeededMirror = true;
  writeSessionStorage(
    TEAM_INTENT_KEY,
    `${kind}${TEAM_INTENT_SEPARATOR}${parkedAtMs}${TEAM_INTENT_SEPARATOR}${teamId}`,
  );
  captureAuthReturnRoute({ route: buildViewTeamPath(teamId) });
}

export function clearParkedTeamIntent(): void {
  const releasedIntent = seededIntent();
  mirroredIntent = null;
  hasSeededMirror = true;
  removeSessionStorage(TEAM_INTENT_KEY);
  // The route exists to serve this intent. Left behind, it would reopen a team
  // for a sign-in the user has already abandoned, or open the auth gate on a
  // later visit to the landing. Only THIS intent's route is dropped — a route
  // stashed by a forced sign-out belongs to that interruption, not to this one.
  if (
    releasedIntent != null &&
    peekAuthReturnRoute() === buildViewTeamPath(releasedIntent.teamId)
  ) {
    clearAuthReturnRoute();
  }
}

/**
 * Which live action is waiting for THIS team? An action parked for another team
 * is left alone (it is that team's to resume); an expired one is dropped on
 * sight, so it can never resurrect.
 */
export function getParkedTeamIntentKind({
  teamId,
}: {
  teamId: string;
}): ParkedTeamIntentKind | null {
  const intent = seededIntent();
  if (intent == null) return null;
  if (isExpired(intent)) {
    clearParkedTeamIntent();
    return null;
  }
  return intent.teamId === teamId ? intent.kind : null;
}
